import React, { useState, useRef } from 'react'
import Box from '@mui/joy/Box'
import Grid from '@mui/joy/Grid'
import Sheet from '@mui/joy/Sheet'
import { useLocation } from 'react-router-dom'
import { Editor } from '../components/Editor'
import { TextPostList } from '../components/TextPostList'
import { ItemProgress } from '../components/ItemProgress'
import { SchedulerForm } from '../components/SchedulerForm'
import { CampaignCompleted } from '../components/CampaignCompleted'
import { CampaignAborted } from '../components/CampaignAborted'
import Typography from '@mui/joy/Typography'
import { CampaignAPI } from '../api/CampaignAPI'
import { canvasActions } from '../common/canvasActions'
import _ from 'lodash'

export const NewCampaign = () => {
    const loc = useLocation()
    const messages = loc.state.messages
    const campaignTitle = loc.state.title
    const objectId = loc.state.id
    const objs = messages.map((msg, idx) => ({
        [idx]: {
            message: msg,
            canvas: null,
            postData: null,
        },
    }))
    const map = Object.assign({}, ...objs)
    const [items, setItems] = useState(map)
    const [activeId, setActiveId] = useState(Object.keys(objs)[0])
    const [complete, setComplete] = useState(false)
    const [cancelled, setCancelled] = useState(false)
    const campaignId = useRef(null)
    const assetImg = useRef(null)

    const handleOnClick = (id) => {
        console.log('changing active item', items[id])
        setActiveId(id)
    }

    const setNextActive = (prevId) => {
        let keys = Object.keys(items)
        let nextIdx = keys.find((key, idx) => {
            return key !== prevId && items[key].postData === null
        })
        if (nextIdx) {
            setActiveId(nextIdx)
        } else if (keys.length === 0) {
            setCancelled(true)
        } else {
            setComplete(true)
        }
    }

    const handleDelete = (id) => {
        delete items[id]
        setItems({ ...items })
        setNextActive(id)
    }

    const handleCanvasChange = (id, canvas) => {
        assetImg.current = canvas
    }

    const handleSubmit = (id, postData) => {
        const dataUrl = canvasActions.takeScreenshot(assetImg.current)
        const assetObj = {
            caption: postData.caption,
            schedule_date: postData.schedule_date,
            publish_to: _.flatMap(
                postData.publishTo,
                (obj) => obj.canonicalName
            ),
        }

        if (campaignId.current === null) {
            CampaignAPI.createQuotesCampaign(objectId).then((response) => {
                campaignId.current = response.data.id
                CampaignAPI.createAsset(
                    campaignId.current,
                    dataUrl,
                    assetObj,
                    assetImg.current.toJSON()
                ).then(() => {
                    items[id].postData = postData
                    setItems({ ...items })
                    setNextActive(id)
                    assetImg.current = null
                })
            })
        } else {
            CampaignAPI.createAsset(
                campaignId.current,
                dataUrl,
                assetObj,
                assetImg.current.toJSON()
            ).then(() => {
                items[id].postData = postData
                setItems({ ...items })
                setNextActive(id)
                assetImg.current = null
            })
        }
    }

    if (complete) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                }}
            >
                <CampaignCompleted id={campaignId.current} />
            </Box>
        )
    } else if (cancelled) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                }}
            >
                <CampaignAborted />
            </Box>
        )
    }

    return (
        <Grid
            container
            direction="row"
            sx={{ height: '100%', width: '100%' }}
            justifyContent="center"
        >
            <Grid container xs={2} sx={{ height: '100%' }} direction="column">
                <Sheet
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        borderWidth: '0 1px 0 0',
                        height: '100%',
                    }}
                    variant="outlined"
                    color="neutral"
                >
                    <Typography
                        color="neutral"
                        level="h3"
                        sx={{ alignSelf: 'flex-start', p: 2 }}
                    >
                        Text Posts
                    </Typography>
                    <TextPostList
                        onItemClick={handleOnClick}
                        onDelete={handleDelete}
                        messages={items}
                        activeId={activeId}
                    />
                    <ItemProgress items={items} />
                </Sheet>
            </Grid>
            <Grid container xs={8} sx={{ height: '100%' }}>
                <Editor
                    postData={items[activeId].message}
                    onChange={handleCanvasChange}
                    id={activeId}
                />
            </Grid>
            <Grid container xs={2} direction="column">
                <Typography
                    color="neutral"
                    level="h3"
                    sx={{ alignSelf: 'flex-start', p: 2 }}
                >
                    Schedule Post
                </Typography>
                <SchedulerForm
                    onSubmit={handleSubmit}
                    key={activeId}
                    id={activeId}
                />
            </Grid>
        </Grid>
    )
}
